import axios from 'axios'
import useAuthStore from '../store/authStore'
import {
  buildLoginPath,
  captureCurrentLocationForLogin,
} from '../utils/authRedirect'
function nestMessageToString(message) {
  if (message == null) return ''
  if (Array.isArray(message)) return message.filter(Boolean).join(' ')
  return String(message)
}

const GENERIC_API_MESSAGES = new Set([
  'internal server error',
  'internal server error.',
  'error',
  'request failed',
])

function isGenericApiMessage(msg) {
  if (!msg || typeof msg !== 'string') return true
  const normalized = msg.trim().toLowerCase()
  if (GENERIC_API_MESSAGES.has(normalized)) return true
  // Axios default when response body is empty / unreadable
  if (/^request failed with status code \d+$/i.test(normalized)) return true
  return false
}

/** Nest / Express style JSON error bodies */
export function extractApiErrorMessage(data) {
  if (!data || typeof data !== 'object') return ''
  const candidates = [
    nestMessageToString(data.message),
    typeof data.detail === 'string' ? data.detail : '',
    typeof data.error === 'string' ? data.error : '',
    typeof data.exception === 'string' ? data.exception : '',
  ].filter(Boolean)

  for (const c of candidates) {
    if (!isGenericApiMessage(c)) return c
  }
  // Prefer a non-empty generic over nothing so UI still shows something
  return candidates[0] || ''
}

/**
 * Prefer Nest response body over axios's "Request failed with status code N".
 * @param {unknown} err
 * @param {string} [fallback]
 */
export function getApiErrorMessage(err, fallback = 'Request failed') {
  const fromBody = extractApiErrorMessage(err?.response?.data)
  if (fromBody && !isGenericApiMessage(fromBody)) return fromBody
  const axiosMsg = err?.message
  if (axiosMsg && !isGenericApiMessage(axiosMsg)) return String(axiosMsg)
  if (fromBody) return fromBody
  return fallback
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000'

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add token to requests
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Let the browser set multipart/form-data with the correct boundary.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type']
    delete config.headers['content-type']
  }
  return config
})

// Handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = error.response?.data
    if (data && typeof data === 'object') {
      const improved = extractApiErrorMessage(data)
      // So every `err.response?.data?.message` call site gets a real message
      // even when Nest originally returned only "Internal server error".
      if (improved && isGenericApiMessage(nestMessageToString(data.message))) {
        error.response.data = { ...data, message: improved }
      }
      error.apiMessage = improved || nestMessageToString(data.message)
    }

    if (error.response?.status === 401) {
      const reqUrl = String(error.config?.url || '')
      const isPublicAuth =
        /\/admin\/auth\/(login|forgot-password|accept-invite|reset-password|invite\/validate|reset-password\/validate)/.test(
          reqUrl,
        )
      if (!isPublicAuth) {
        const detail = extractApiErrorMessage(error.response?.data)
        useAuthStore.getState().logout()
        captureCurrentLocationForLogin()
        const path = `${window.location.pathname}${window.location.search}${window.location.hash}`
        const extra = { reason: 'auth_failed' }
        if (detail) {
          extra.hint = encodeURIComponent(detail.slice(0, 450))
        }
        window.location.assign(buildLoginPath(path, extra))
      }
    }
    // 403: missing permission — do not clear session; callers show message
    return Promise.reject(error)
  }
)

export default apiClient
